import type { LegalAiConsultHandoffAttachmentVo, LegalAiConsultHandoffVo } from '@/types/api'

export type AiHandoffDetailSection = 'summary' | 'attachments' | 'history'

export interface AiHandoffSelection {
  handoffId: string
  sessionId: string
  section: AiHandoffDetailSection
  attachmentId?: string
}

/** URL 切换会话也会立即关闭旧交接详情，不依赖导航事件或本地对象副本。 */
export function getSelectedHandoff(
  handoffs: LegalAiConsultHandoffVo[],
  selection: AiHandoffSelection | null,
  sessionId: string | undefined,
) {
  if (!selection || selection.sessionId !== sessionId) return undefined
  return handoffs.find((handoff) => String(handoff.id) === selection.handoffId)
}

/** 仅生成中的总结持续刷新，完成、失败和未启用均停止轮询。 */
export function getHandoffRefetchInterval(handoffs: LegalAiConsultHandoffVo[] | undefined) {
  return handoffs?.some(
    (handoff) => handoff.summaryStatus === 'pending' || handoff.summaryStatus === 'running',
  )
    ? 3_000
    : false
}

/** 卡片只显示可读文本，完整内容交给现有 MarkdownResponse 渲染。 */
export function getHandoffSummaryPreview(markdown: string | null | undefined): string {
  return (markdown ?? '')
    .replace(/```[^\n]*\n?([\s\S]*?)```/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^\s*(?:#{1,6}\s+|>\s*|[-*+]\s+|\d+[.)]\s+)/gm, '')
    .replace(/[*_`~]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

export function formatHandoffCount(value: string | number | undefined): string {
  const count = Number(value ?? 0)
  return Number.isFinite(count) && count > 0 ? `${Math.round(count)} 条 AI 对话` : 'AI 对话已同步'
}

export function formatHandoffTime(raw: string | undefined): string {
  if (!raw?.trim()) return ''
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('zh-CN', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

export type HandoffAttachmentKind = 'image' | 'pdf' | 'audio' | 'video' | 'file'

export function getHandoffAttachmentKind(
  attachment: Pick<LegalAiConsultHandoffAttachmentVo, 'fileName' | 'contentType' | 'msgType'>,
): HandoffAttachmentKind {
  const contentType = attachment.contentType?.toLowerCase() ?? ''
  const name = attachment.fileName?.toLowerCase() ?? ''
  if (
    contentType.startsWith('image/') ||
    Number(attachment.msgType) === 2 ||
    /\.(png|jpe?g|webp|gif|avif|bmp|heic)$/.test(name)
  )
    return 'image'
  if (contentType === 'application/pdf' || name.endsWith('.pdf')) return 'pdf'
  if (contentType.startsWith('audio/') || /\.(mp3|m4a|wav|ogg|aac)$/.test(name)) return 'audio'
  if (contentType.startsWith('video/') || /\.(mp4|webm|mov)$/.test(name)) return 'video'
  return 'file'
}

/** 不允许附件访问地址执行脚本；实际访问权始终由交接附件接口确认。 */
export function resolveHandoffAttachmentUrl(raw: string, origin: string): URL {
  if (!raw.trim()) throw new Error('材料访问地址不可用')
  const url = new URL(raw, origin)
  if (!['http:', 'https:'].includes(url.protocol)) throw new Error('材料访问地址不可用')
  return url
}
